/* bundle-cart.js — wires bundle-selector.liquid into PDP price + ATC + cart.
 *
 * Responsibilities:
 *   A) Live price layer — recompute and update [data-price],
 *      [data-price-compare], [data-save-pill], .atc-button--price whenever
 *      a bundle checkbox toggles or the parent variant changes.
 *   B) Quantity lock — force parent qty to 1 whenever any add-on is checked.
 *   C) Form-submit interceptor — bulk POST /cart/add.js with the parent +
 *      checked children, grouped by a shared _bundle_id property.
 *   D) Parent-removal cleanup — when a bundle parent line is removed from
 *      the cart, cascade-remove its children.
 *
 * Loaded from layout/theme.liquid (deferred). No-ops on pages without a
 * bundle-selector or without window.toptureParentPricing.
 */
(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Shared helpers — used on every page (cart removal cleanup runs site-wide)
  // -------------------------------------------------------------------------

  // Whole-dollar amounts drop ".00" here rather than relying on the
  // Shopify.formatMoney wrapper in product-form.liquid: that wrapper only
  // exists on PDPs, and Shopify's api.jquery.js defines formatMoney after
  // DOMContentLoaded, so our first writes got the raw formatter (or the
  // toFixed fallback) and printed "$1,181.00" in the sticky bar and drawer.
  function formatMoney(cents) {
    var fmt = (window.toptureParentPricing && window.toptureParentPricing.moneyFormat)
      || (window.Shopify && Shopify.money_format)
      || '${{amount}}';
    var out = (window.Shopify && typeof Shopify.formatMoney === 'function')
      ? Shopify.formatMoney(cents, fmt)
      : '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return out.replace(/\.00(?=\D|$)/g, '');
  }

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({ credentials: 'same-origin' }, opts || {}))
      .then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return null; }).then(function (body) {
            var err = new Error('HTTP ' + r.status);
            err.body = body;
            throw err;
          });
        }
        return r.json();
      });
  }

  function getLineBundleMeta(line) {
    // line.properties can be an object or array depending on Shopify endpoint.
    if (!line || !line.properties) return { bundleId: null, role: null };
    var props = line.properties;
    var bundleId = null;
    var role = null;
    if (Array.isArray(props)) {
      for (var i = 0; i < props.length; i++) {
        if (props[i][0] === '_bundle_id') bundleId = props[i][1];
        if (props[i][0] === '_bundle_role') role = props[i][1];
      }
    } else {
      bundleId = props._bundle_id || null;
      role = props._bundle_role || null;
    }
    return { bundleId: bundleId, role: role };
  }

  // -------------------------------------------------------------------------
  // (D) Parent-removal cascade — runs site-wide on cart page
  // -------------------------------------------------------------------------
  // Intercepts [data-cartitem-remove] clicks in capture phase, BEFORE empire's
  // own handler at empire-core.js:18471. Bundle-parent status is stamped
  // server-side on the line (data-bundle-role/data-bundle-id in
  // cart-item.liquid) so the block-or-pass decision is SYNCHRONOUS — event
  // dispatch is synchronous, so a preventDefault issued from inside a .then()
  // lands after empire's handler has already fired and both removals race.

  document.addEventListener('click', function (e) {
    var removeLink = e.target.closest && e.target.closest('[data-cartitem-remove]');
    if (!removeLink) return;
    var lineEl = removeLink.closest('[data-cartitem]');
    if (!lineEl) return;
    var lineKey = lineEl.getAttribute('data-cartitem-key');
    if (!lineKey) return;
    if (lineEl.getAttribute('data-bundle-role') !== 'parent') return;
    var bundleId = lineEl.getAttribute('data-bundle-id');
    if (!bundleId) return;

    // It IS a parent — stop empire's individual-line removal handler NOW,
    // then cascade-remove every line sharing this bundleId in one atomic
    // /cart/update.js POST.
    e.preventDefault();
    e.stopImmediatePropagation();

    fetchJSON('/cart.js').then(function (cart) {
      var updates = {};
      (cart.items || []).forEach(function (it) {
        var m = getLineBundleMeta(it);
        if (m.bundleId === bundleId) updates[it.key] = 0;
      });
      // Stamped attrs can go stale after a cart mutation without re-render;
      // fall back to removing at least the clicked line.
      if (!Object.keys(updates).length) updates[lineKey] = 0;

      return fetchJSON('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    }).then(function (newCart) {
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: newCart }));
      // Empire re-renders the cart on cartcount:update on the cart page,
      // but as a belt-and-suspenders for the cart-page layout, reload.
      // Cart page only — PDPs don't show cart items inline.
      if (document.querySelector('[data-cartitem]')) {
        window.location.reload();
      }
    }).catch(function (err) {
      // eslint-disable-next-line no-console
      console.error('[bundle-cart] cascade removal failed', err);
      // Last resort: remove just the clicked line directly. Never re-dispatch
      // the click — it would re-enter this handler (or race empire) again.
      fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      }).then(function () { window.location.reload(); })
        .catch(function () { /* leave the page as-is; user can retry */ });
    });
  }, true);

  // -------------------------------------------------------------------------
  // PDP-only wiring — bail early if not on a product page with bundle-selector
  // -------------------------------------------------------------------------

  var productForm = document.querySelector('form[action*="/cart/add"][data-product-form], form[action*="/cart/add"]');
  var bundleRoot = document.querySelector('[data-bundle-selector]');
  var parentPricing = window.toptureParentPricing;

  // The cart preview ships on every PDP via product-form.liquid, but bundle-
  // specific behaviors (price layer, qty lock, submit interceptor, cart
  // cascade-remove) only apply when [data-bundle-selector] is present.
  // We need to keep running for the cart-preview case even when bundleRoot
  // is absent, so split the bail-out into two stages.
  if (!productForm || !parentPricing) return;

  // Scope DOM writes to the main product details wrapper so we don't clobber
  // price spans inside related-product cards, recently-viewed, complementary,
  // or other product cards on the same page. See product.liquid:125 — every
  // PDP variant of this snippet renders inside <div data-product-details>.
  // Anchor on the form when there's no bundle-selector to scope from.
  var productScope = (bundleRoot && bundleRoot.closest('[data-product-details]'))
    || productForm.closest('[data-product-details]')
    || document;

  var variantSelection = productScope.querySelector('[data-variant-selection]');

  // Which variant should the next recompute price?
  //
  // NOT `[name="id"]` first. empire-product.js writes that <select> inside
  // `_switchVariant()` (empire-product.js:1938), and every signal we listen to
  // fires BEFORE `_switchVariant` runs — our listeners are registered ahead of
  // empire's on the same element. Reading the select first therefore priced
  // every change one variant late: the sticky bar, the cart-preview rows and
  // the ATC button all kept the PREVIOUS variant's number forever, because no
  // later signal ever re-ran them. Proved with auto/tests/_atc-price-diag.js.
  //
  // Order of trust:
  //   1. the id handed to us by the `variant-change` event detail — authoritative
  //   2. <variant-selection variant="…">, which the custom element updates
  //      before empire's handler touches the <select>
  //   3. the <select name="id"> — correct once everything has settled, and the
  //      only source available on the initial page-load pass
  function readVariantId(explicitId) {
    if (explicitId && parentPricing.variants[explicitId]) return String(explicitId);
    var attr = variantSelection && variantSelection.getAttribute('variant');
    if (attr && parentPricing.variants[attr]) return attr;
    var idInput = productForm.querySelector('[name="id"]');
    return idInput && idInput.value;
  }

  // -------------------------------------------------------------------------
  // (A) Live price layer
  // -------------------------------------------------------------------------

  var SAVE_PILL_LABEL = 'Save ';

  // Shipping cents (numeric only; 0 when free or "Calculated at checkout").
  // Sourced from product-form.liquid's `[data-cart-preview]` wrapper attribute
  // so the JS and Liquid render the same number. Folded into the
  // `.atc-button--price` and `.sticky-atc-bar__price` displays so customers
  // see the full charge on the buttons, matching the cart-preview total row.
  // Per-product (not per-variant) — read once and cached.
  var cartPreviewElForShipping = document.querySelector('[data-cart-preview]');
  var cartPreviewShippingCents = cartPreviewElForShipping
    ? (parseInt(cartPreviewElForShipping.getAttribute('data-cart-preview-shipping-cents'), 10) || 0)
    : 0;

  function getParentCents(variantId) {
    // Look up cents from the server-rendered map in window.toptureParentPricing.
    // See readVariantId() for why the <select name="id"> is the LAST resort.
    var vid = readVariantId(variantId);
    var entry = vid && parentPricing.variants[vid];
    if (!entry) {
      // Fallback to first variant (default-selected case)
      var firstKey = Object.keys(parentPricing.variants)[0];
      entry = parentPricing.variants[firstKey];
    }
    return {
      price: entry ? entry.price : 0,
      compareAt: entry ? entry.compareAt : 0
    };
  }

  function getCheckedAddOns() {
    if (!bundleRoot) return [];
    var checked = bundleRoot.querySelectorAll('[data-bundle-input]:checked');
    var out = [];
    for (var i = 0; i < checked.length; i++) {
      var el = checked[i];
      out.push({
        variantId: el.value,
        price: parseInt(el.getAttribute('data-variant-price'), 10) || 0,
        compareAt: parseInt(el.getAttribute('data-variant-compare-at'), 10) || 0,
        title: el.getAttribute('data-variant-title') || '',
        available: el.getAttribute('data-variant-available') === 'true'
      });
    }
    return out;
  }

  function recomputeAndRender(variantId) {
    var parent = getParentCents(variantId);
    var addOns = getCheckedAddOns();

    var totalPrice = parent.price;
    var totalCompareAt = parent.compareAt;
    addOns.forEach(function (a) {
      totalPrice += a.price;
      // Use compare_at if set; otherwise the add-on contributes equally to
      // both totals (no fake bundle "savings").
      totalCompareAt += a.compareAt;
    });

    var savings = totalCompareAt - totalPrice;
    var hasSale = savings > 0;

    // Main price — write ONLY to the visible span inside
    // [data-price-container]. product-price.liquid renders a parallel hidden
    // mirror at [data-current-price-hidden] containing another [data-price]
    // span that empire-core uses as a write target; touching it would
    // (a) overwrite empire's variant-change state and (b) infinite-loop
    // with the MutationObserver in product-form.liquid.
    productScope.querySelectorAll('[data-price-container] [data-price]').forEach(function (el) {
      if (el.matches('[data-price-min], [data-price-max]')) return;
      el.textContent = formatMoney(totalPrice);
    });

    // Compare-at — same rule. Only the visible one inside
    // [data-price-compare-container]; skip the [data-compare-price-hidden]
    // mirror.
    productScope.querySelectorAll('[data-price-compare-container] [data-price-compare]').forEach(function (el) {
      el.textContent = formatMoney(totalCompareAt);
    });

    // Compare-at container visibility — Liquid only adds .visible if the
    // *initial* state had a sale. We need to manage it post-bundle-toggle.
    productScope.querySelectorAll('[data-price-compare-container]').forEach(function (el) {
      el.classList.toggle('visible', hasSale);
    });

    // Save pill — also lives next to the compare-at container, visible only.
    productScope.querySelectorAll('[data-save-pill]').forEach(function (el) {
      if (hasSale) {
        el.classList.remove('price__save-pill--hidden');
        el.textContent = SAVE_PILL_LABEL + formatSavings(savings);
      } else {
        el.classList.add('price__save-pill--hidden');
        el.textContent = '';
      }
    });

    // Gallery savings badge overlay (first product image) — mirror the save
    // pill so the on-image savings tracks the configured bundle total, exactly
    // like the pricing pill. Uses the gallery badge's own hidden class. The
    // gallery lives in the left column, OUTSIDE productScope ([data-product-details]),
    // so widen the query to the enclosing [data-product-wrapper].
    var galleryBadgeScope = productScope.closest('[data-product-wrapper]') || document;
    galleryBadgeScope.querySelectorAll('[data-gallery-save-pill]').forEach(function (el) {
      if (hasSale) {
        el.classList.remove('product-gallery--badge--hidden');
        el.textContent = SAVE_PILL_LABEL + formatSavings(savings);
      } else {
        el.classList.add('product-gallery--badge--hidden');
        el.textContent = '';
      }
    });

  }

  // ATC button price text.
  //
  // Deliberately NOT part of recomputeAndRender: that only runs on products
  // that have a bundle-selector, but every PDP renders `.atc-button--price`
  // from Liquid, so a plain multi-variant product needs this on variant change
  // too — otherwise the button keeps quoting the variant the page loaded with.
  // Nothing in empire writes this span, so there's no handler to conflict with.
  //
  // Re-query inside the form at call time (Bold replaces the button at
  // runtime; see .planning/gotchas.md "Bold app re-renders the PDP ATC button").
  // Includes shipping so the button shows the full charge — matches the
  // cart-preview "Total" row.
  // Boards ordered, on products whose quantity selector is shown (sauna wood,
  // snippets/product-form.liquid). Everywhere else the selector is hidden and
  // the product sells one at a time, so this is 1. Shipping is per order, so
  // only the product price is multiplied.
  function shownQuantity() {
    var input = productForm.querySelector('.quantity-selector--shown [data-quantity-input]');
    var qty = input ? parseInt(input.value, 10) : 1;
    return qty > 0 ? qty : 1;
  }

  function updateAtcButtonPrice(variantId) {
    var atcPriceEl = productForm.querySelector('.atc-button--price');
    if (!atcPriceEl) return;
    var total = getParentCents(variantId).price * shownQuantity();
    getCheckedAddOns().forEach(function (a) { total += a.price; });
    // The Liquid template writes "  $X" with leading spaces — preserve a
    // single leading space for consistent spacing with the bullet.
    atcPriceEl.textContent = ' ' + formatMoney(total + cartPreviewShippingCents);
  }

  // Sticky ATC bar mirror.
  //
  // The sticky bar (snippets/sticky-atc-bar.liquid) has its own price,
  // compare-at, and a Shop Pay installments widget rendered in PRODUCT
  // mode. Lives in its own function (not inside recomputeAndRender)
  // because recomputeAndRender only runs when bundleRoot exists; the
  // sticky bar also needs to update on plain variant changes on
  // products without a bundle-selector.
  //
  // The installments widget normally maps `variant-id` -> a real entry
  // in `shopify-meta.variants[]` and reads its `full_price` to compute
  // the monthly via Shopify's bundled `ge()` amortization function.
  // Bundle add-ons don't correspond to a single Shopify variant id, so
  // we synthesize a fake variant entry whose `full_price` is the
  // bundle-augmented total, then point variant-id at it. The widget
  // computes everything itself — banner + "View sample plans" modal
  // both reflect the synthetic full_price. No client-side math, no
  // shadow-DOM price override, no `[data-cart-subtotal]` plumbing.
  //
  // A unique synthetic id is used per update so the widget's
  // observed-attribute change detection fires every time. Old synthetic
  // entries are scrubbed before adding the new one to keep the meta
  // from growing without bound.
  var STICKY_SYNTH_BASE = 9990000000;
  var stickySynthCounter = 0;

  // Savings amounts in the "Save $X" pills drop a trailing .00 (whole dollars
  // show no cents) but keep real cents (e.g. $1,004.99), with comma grouping —
  // matching the server's money_without_trailing_zeros and static-product.liquid.
  // Generic formatMoney/Shopify.formatMoney can emit ".00" and inconsistent
  // grouping, so the save pills use this instead.
  function formatSavings(cents) {
    var n = Math.round(cents);
    var dollars = Math.floor(n / 100);
    var rem = n % 100;
    var d = dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return rem === 0 ? '$' + d : '$' + d + '.' + (rem < 10 ? '0' + rem : rem);
  }
  function formatMoneyWithCents(cents) {
    // shopify-installments parses dollar strings with its own regex, so
    // either "$X,XXX" or "$X,XXX.00" works for math. We pass with cents
    // to look like a normal Shopify-formatted price in case the modal
    // re-displays it verbatim.
    var fmt = (window.toptureParentPricing && window.toptureParentPricing.moneyFormat)
      || (window.Shopify && Shopify.money_format) || '${{amount}}';
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      // Force the cents-bearing variant of the format if available.
      var withCents = fmt.replace(/amount(_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator)?/g, 'amount');
      return Shopify.formatMoney(cents, withCents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Apply the synthetic-variant trick to ONE installments widget so it
  // displays the monthly for `totalPriceCents`. Pure DOM mutation; no
  // client-side amortization math. See header comment above for the
  // rationale.
  //
  // Flicker avoidance:
  //   1. Each widget remembers the last total it was set to. We skip
  //      the whole operation when the total hasn't changed -- multiple
  //      events fire per option-click (topture:bundle:change +
  //      variant-change + form change), and re-running this on the
  //      same total would just generate another unique variant-id +
  //      re-renders + visible flash.
  //   2. We do NOT scrub previous synthetic entries from
  //      meta.variants. There's a microtask-sized window between
  //      `setAttribute('shopify-meta', ...)` and
  //      `setAttribute('variant-id', ...)` during which the widget may
  //      render: if the OLD variant-id no longer resolves to anything
  //      in the variants list (because we scrubbed it), the widget
  //      falls back to its "Pay in 4 for orders over $35.00" marketing
  //      banner -- the user sees that as a flash. By keeping old
  //      synths around (they're tiny, ~100 bytes each, and rarely
  //      accumulate beyond a handful), the old variant-id is still
  //      resolvable until the new variant-id takes effect.
  function applySyntheticVariant(widget, totalPriceCents) {
    if (!widget) return;
    if (widget._stickySyntheticTotal === totalPriceCents) return;
    var meta;
    try { meta = JSON.parse(widget.getAttribute('shopify-meta') || 'null'); }
    catch (e) { meta = null; }
    if (!meta || !meta.variants) return;
    var refVariant = meta.variants[0] || {};
    var nTerms = refVariant.number_of_payment_terms || 2;
    stickySynthCounter++;
    var synthId = STICKY_SYNTH_BASE + stickySynthCounter;
    meta.variants.push({
      id: synthId,
      full_price: formatMoneyWithCents(totalPriceCents),
      price_per_term: formatMoneyWithCents(
        Math.round(totalPriceCents / Math.max(1, nTerms))
      ),
      eligible: true,
      available: true,
      number_of_payment_terms: nTerms,
    });
    widget.setAttribute('shopify-meta', JSON.stringify(meta));
    widget.setAttribute('variant-id', String(synthId));
    widget._stickySyntheticTotal = totalPriceCents;
  }

  // Find the visible MAIN PDP installments widget. Empire's product JS
  // moves the server-rendered widget from [data-payment-terms-reference]
  // (hidden source) into [data-payment-terms-target] (visible slot) on
  // page init — see empire-core.js around L24020 for the move. We
  // select on the target slot so we get the one users actually see, and
  // explicitly exclude the sticky bar's, free-bonuses', and hidden
  // reference's widgets.
  function findMainInstallmentsWidget() {
    var scope = document.querySelector('[data-product-details]') || document;
    var w = scope.querySelector('[data-payment-terms-target] shopify-payment-terms');
    if (w) return w;
    // Fallback: any non-sticky/non-bonus/non-reference widget under the product details
    var all = scope.querySelectorAll('shopify-payment-terms');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.closest('[data-sticky-atc-bar]')) continue;
      if (el.closest('.tt-fb__financing')) continue;
      if (el.closest('[data-payment-terms-reference]')) continue;
      return el;
    }
    return null;
  }

  function updateStickyBar(variantId) {
    var parent = getParentCents(variantId);
    var addOns = getCheckedAddOns();
    var totalPrice = parent.price;
    var totalCompareAt = parent.compareAt;
    addOns.forEach(function (a) {
      totalPrice += a.price;
      totalCompareAt += a.compareAt;
    });
    var hasSale = totalCompareAt > totalPrice;

    // Sticky bar: price + compare-at + installments. Price, compare-at AND
    // installments all include shipping so every number under the sticky
    // ATC reflects what the customer will actually be charged — matches the
    // cart-preview "Total" row and the main ATC button.
    var stickyBar = document.querySelector('[data-sticky-atc-bar]');
    var totalWithShipping = totalPrice + cartPreviewShippingCents;
    if (stickyBar) {
      var stickyPrice = stickyBar.querySelector('.sticky-atc-bar__price');
      if (stickyPrice) stickyPrice.textContent = formatMoney(totalWithShipping);
      var stickyCompare = stickyBar.querySelector('.sticky-atc-bar__compare-price');
      if (stickyCompare) {
        if (hasSale) {
          stickyCompare.textContent = formatMoney(totalCompareAt + cartPreviewShippingCents);
          stickyCompare.style.display = '';
        } else {
          stickyCompare.style.display = 'none';
        }
      }
      var stickyWidget = stickyBar.querySelector('.sticky-atc-bar__installments shopify-payment-terms');
      applySyntheticVariant(stickyWidget, totalWithShipping);
    }

    // Main PDP installments widget — the line shown directly under the
    // main price. recomputeAndRender() already updated [data-price] /
    // [data-price-compare-container] for bundle changes; this also
    // refreshes the monthly figure shown below them. Empire-product.js
    // itself sets variant-id on this same widget on real-variant
    // changes — applySyntheticVariant runs AFTER its handler (we're on
    // bundle-cart.js's listener chain, which is bound after empire's
    // initial setup), so our write wins. On variant change with NO
    // bundle add-ons selected, totalPrice equals the new variant's
    // price, so the synthetic full_price matches what empire would have
    // shown anyway — same number, just rendered via our synthetic
    // entry. No visible difference for the user.
    var mainWidget = findMainInstallmentsWidget();
    applySyntheticVariant(mainWidget, totalWithShipping);
  }

  // -------------------------------------------------------------------------
  // Cart preview — live summary block rendered below the shipping-eta-box.
  // Mirrors the bundle checkboxes as addon rows and recomputes the total
  // on every bundle change / variant change. Shipping is server-rendered
  // (it depends on product metafields, not bundle state) and folded into
  // the total via the `data-cart-preview-shipping-cents` attribute; 0 when
  // shipping is free or "Calculated at checkout".
  // -------------------------------------------------------------------------

  var cartPreviewEl = productScope.querySelector('[data-cart-preview]');

  function updateCartPreview(variantId) {
    if (!cartPreviewEl) return;
    var parent = getParentCents(variantId);
    var addOns = getCheckedAddOns();

    var mainPriceEl = cartPreviewEl.querySelector('[data-cart-preview-main-price]');
    if (mainPriceEl) mainPriceEl.textContent = formatMoney(parent.price);

    var addonsContainer = cartPreviewEl.querySelector('[data-cart-preview-addons]');
    if (addonsContainer) {
      // Rebuild from scratch — small lists (typically 0-5 items), diffing
      // would add complexity without meaningful perf win.
      addonsContainer.replaceChildren();
      addOns.forEach(function (a) {
        var row = document.createElement('div');
        row.className = 'cart-preview__row cart-preview__row--addon';
        row.dataset.variantId = a.variantId;

        var label = document.createElement('span');
        label.className = 'cart-preview__label';
        label.textContent = a.title;

        var price = document.createElement('span');
        price.className = 'cart-preview__price money';
        price.textContent = formatMoney(a.price);

        row.appendChild(label);
        row.appendChild(price);
        addonsContainer.appendChild(row);
      });
    }

    var total = parent.price + cartPreviewShippingCents;
    addOns.forEach(function (a) { total += a.price; });
    var totalEl = cartPreviewEl.querySelector('[data-cart-preview-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);
  }

  // -------------------------------------------------------------------------
  // (B) Quantity lock
  // -------------------------------------------------------------------------

  function syncQtyLock() {
    var anyChecked = bundleRoot.querySelector('[data-bundle-input]:checked');
    var qtyWrappers = productForm.querySelectorAll('[data-quantity-selector]');
    qtyWrappers.forEach(function (wrap) {
      var input = wrap.querySelector('[data-quantity-input]');
      var minus = wrap.querySelector('[data-quantity-minus]');
      var plus = wrap.querySelector('[data-quantity-plus]');
      if (anyChecked) {
        if (input) {
          input.value = 1;
          input.setAttribute('readonly', 'readonly');
          input.dataset.bundleLocked = 'true';
        }
        if (minus) minus.setAttribute('disabled', 'disabled');
        if (plus) plus.setAttribute('disabled', 'disabled');
        wrap.classList.add('quantity-selector--bundle-locked');
      } else {
        if (input && input.dataset.bundleLocked === 'true') {
          input.removeAttribute('readonly');
          delete input.dataset.bundleLocked;
        }
        if (minus) minus.removeAttribute('disabled');
        if (plus) plus.removeAttribute('disabled');
        wrap.classList.remove('quantity-selector--bundle-locked');
      }
    });
  }

  // -------------------------------------------------------------------------
  // (C) Form-submit interceptor — bulk POST /cart/add.js
  // -------------------------------------------------------------------------

  function generateBundleId() {
    if (window.crypto && typeof crypto.randomUUID === 'function') {
      return 'b_' + crypto.randomUUID();
    }
    return 'b_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  }

  function setAtcProcessing(on) {
    // Re-query [data-product-atc] at use time (Bold replaces it). Toggle the
    // 'processing' class to match empire's spinner pattern.
    var atc = productForm.querySelector('[data-product-atc]');
    if (!atc) return;
    if (on) atc.classList.add('processing');
    else atc.classList.remove('processing');
  }

  function showErrorBanner(message) {
    // Minimal inline error — empire's banner system requires deeper hooks.
    // For MVP, fall back to alert + console.
    // eslint-disable-next-line no-alert
    window.alert(message || 'Something went wrong adding this bundle to your cart. Please try again.');
  }

  // Document-level CAPTURE phase so we fire BEFORE empire-product's
  // submit listener (empire binds `submit.product-details-<id>` via jQuery
  // at form level in bubble phase — see empire-product.js:1923). Without
  // this, both handlers run on the same submit: my code POSTs the bundle
  // AND empire posts the base variant alone, leaving a duplicate parent
  // line in the cart.
  document.addEventListener('submit', function (e) {
    if (e.target !== productForm) return;

    // Only intercept when there's actually a bundle selection to add. If
    // every section's state is empty we let the native form submit run so
    // base-product purchases on non-bundle PDPs are unaffected.
    var addOns = getCheckedAddOns();
    if (addOns.length === 0) return;

    e.preventDefault();
    // Halt empire's bubble-phase submitHandler so it can't double-add.
    e.stopImmediatePropagation();
    e.stopPropagation();

    // Preflight: bail if any selected add-on is unavailable. /cart/add.js
    // bulk is NOT atomic; a 422 mid-stream would leave the cart half-built.
    var unavailable = addOns.filter(function (a) { return !a.available; });
    if (unavailable.length) {
      showErrorBanner(
        'One of the selected bundle items is currently sold out: '
        + unavailable.map(function (a) { return a.title; }).join(', ')
      );
      return;
    }

    var idInput = productForm.querySelector('[name="id"]');
    var parentVariantId = idInput && idInput.value;
    if (!parentVariantId) {
      showErrorBanner('Please select a product option before adding to cart.');
      return;
    }

    var bundleId = generateBundleId();

    // Build a human-friendly summary for the cart-visible parent property.
    // cart-item.liquid renders non-underscore-prefixed properties with a
    // green-check "(Included)" badge — one entry per line item.
    var items = [];
    var parentProperties = {
      '_bundle_id': bundleId,
      '_bundle_role': 'parent'
    };
    addOns.forEach(function (a, i) {
      // Per-child labelled property — numbered so multiple add-ons each
      // get their own green-check row in cart-item.liquid:131-153.
      parentProperties['Includes ' + (i + 1)] = a.title;
    });
    items.push({
      id: parseInt(parentVariantId, 10),
      quantity: 1,
      properties: parentProperties
    });
    addOns.forEach(function (a) {
      items.push({
        id: parseInt(a.variantId, 10),
        quantity: 1,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_role': 'child',
          '_bundle_parent_variant': String(parentVariantId)
        }
      });
    });

    setAtcProcessing(true);

    var cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url)
      ? window.Theme.routes.cart_add_url + '.js'
      : '/cart/add.js';
    var cartUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url)
      ? window.Theme.routes.cart_url
      : '/cart';

    fetchJSON(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function () {
      // The add is ACCEPTED from here on — everything below (cart refresh,
      // drawer reveal) is best-effort presentation. Its own catch falls back
      // to navigating to /cart instead of the failure alert: the items ARE
      // in the cart, so "Could not add this bundle" would be a false report.
      return fetchJSON(cartUrl + '.js').then(function (cart) {
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
        // Slide-cart path: when the drawer is enabled and present, auto-open
        // it instead of navigating to /cart. open() internally refreshes +
        // reconciles (see cart-drawer.js openDrawer), so a single open() call
        // re-renders from the live cart and checks the added bundle's
        // reconciled children — no separate refresh() needed (avoids a
        // double-fetch / flash).
        if (window.Theme && window.Theme.slideCartEnabled && window.ToptureCartDrawer) {
          // cart-drawer.js also wraps fetch and may have already opened the
          // drawer for this same /cart/add.js POST. open() is idempotent and
          // re-checks is-open internally, so the second call is a cheap no-op
          // when already open — no double refresh/flash.
          var dz = document.querySelector('[data-cart-drawer]');
          if (!dz || !dz.classList.contains('is-open')) {
            window.ToptureCartDrawer.open();
          }
          setAtcProcessing(false);
          return;
        }
        // Fallback (slide cart OFF): match empire's success flow and navigate
        // to /cart so the customer sees the bundle.
        window.location.href = cartUrl;
      }).catch(function (err) {
        // eslint-disable-next-line no-console
        console.error('[bundle-cart] cart refresh after successful add failed', err);
        window.location.href = cartUrl;
      });
    }).catch(function (err) {
      setAtcProcessing(false);
      // eslint-disable-next-line no-console
      console.error('[bundle-cart] /cart/add.js failed', err);
      var msg = (err && err.body && err.body.description) || 'Could not add this bundle to your cart.';
      showErrorBanner(msg);
    });
  }, true);

  // -------------------------------------------------------------------------
  // Wiring — subscribe to the events that should recompute
  // -------------------------------------------------------------------------
  //
  // Two layers:
  //   - Bundle-only: recomputeAndRender + syncQtyLock. These touch the
  //     main PDP price block (compare-at, save badge) and would conflict
  //     with empire-product.js's own variant handler on non-bundle
  //     products, so we only run them when a bundle-selector is on the page.
  //   - Always-on: updateCartPreview, updateStickyBar, updateAtcButtonPrice.
  //     Empire writes none of those targets, so they're safe everywhere, and
  //     every PDP renders them — a plain multi-variant product needs them
  //     updated on variant change just as much as a bundle one does.
  //
  // `variantId` is optional and only ever supplied by the `variant-change`
  // event; see readVariantId() for why it matters.

  function onBundleOrVariantChange(variantId) {
    if (bundleRoot) {
      recomputeAndRender(variantId);
      syncQtyLock();
    }
    updateCartPreview(variantId);
    updateStickyBar(variantId);
    updateAtcButtonPrice(variantId);
  }

  // Bundle checkbox toggle — only meaningful when a bundle-selector exists.
  // Wrapped so the CustomEvent isn't passed through as a variant id.
  if (bundleRoot) {
    document.addEventListener('topture:bundle:change', function () {
      onBundleOrVariantChange();
    });
  }

  // Parent variant change. Three signals, each covering a gap the others miss:
  //   1. `variant-change` custom event on [data-variant-selection] — the
  //      canonical PixelUnion signal (see empire-product.js:1830). Fires
  //      whenever a variant resolves, including initial load and option
  //      changes that hit a different variant. Its `detail` is
  //      `{product, variant, state}` (see empire-core ~1506) — `detail.variant`
  //      is the only source that's guaranteed current at this point in the chain.
  //   2. Native form `change` events — catches old-school <select name="id">
  //      and any input whose name resolves to "id". Option radios use
  //      names like "Wattage"/"Size" so we also recompute when they change
  //      INSIDE [data-variant-selection] (not for unrelated form inputs
  //      like recipient name etc).
  //   3. MutationObserver on the `variant` attribute (set up below) — covers
  //      themes where the custom element re-sets the attribute without
  //      firing variant-change.
  if (variantSelection) {
    variantSelection.addEventListener('variant-change', function (e) {
      var detail = e && e.detail;
      onBundleOrVariantChange(detail && ((detail.variant && detail.variant.id) || detail.id));
    });
  }

  // Quantity changes on a shown selector re-price the button. The +/- buttons
  // set the input's value without firing an event, so listen for their clicks
  // too and read the value after the theme's own handler has run.
  if (productForm.querySelector('.quantity-selector--shown')) {
    productForm.addEventListener('input', function (e) {
      if (e.target && e.target.matches('.quantity-selector--shown [data-quantity-input]')) updateAtcButtonPrice();
    });
    productForm.addEventListener('click', function (e) {
      if (e.target && e.target.closest('.quantity-selector--shown [data-quantity-plus], .quantity-selector--shown [data-quantity-minus]')) {
        setTimeout(function () { updateAtcButtonPrice(); }, 0);
      }
    });
  }

  productForm.addEventListener('change', function (e) {
    if (!e.target) return;
    var insideVariantSelection = variantSelection && variantSelection.contains(e.target);
    if (e.target.name === 'id' || insideVariantSelection) {
      onBundleOrVariantChange();
    }
  });

  // Signal #3 — belt-and-suspenders: observe the `variant` attribute on
  // [data-variant-selection]. Covers themes where the custom element
  // re-sets that attribute without firing the `variant-change` event.
  if (variantSelection) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].type === 'attributes' && muts[i].attributeName === 'variant') {
          onBundleOrVariantChange();
          return;
        }
      }
    }).observe(variantSelection, { attributes: true, attributeFilter: ['variant'] });
  }

  // Initial pass — defaults checked in Liquid (`{% if is_default %}checked{% endif %}`)
  // need to be reflected in the displayed price + cart preview on page load.
  // Run after DOM ready so the bundle-selector IIFE has had a tick.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      onBundleOrVariantChange();
    });
  } else {
    onBundleOrVariantChange();
  }
})();
